import React, {useCallback, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, useStyleConfig} from '@chakra-ui/react'
import {HeroBannerV2Text} from './hero-banner-v2-text'
import HeroBannerV2BentoBox from './hero-banner-v2-bento-box'
import {remCalc} from '../../../../../plugins/px-to-rem-babel-custom-plugin/utils/remCalc'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {calculateVerticalPadding, getHerobannerGradient} from '../utils'
import {Helmet} from 'react-helmet'
import {
    createCompleteImageSet,
    getAspectRatioObjectFromImageSet,
    getDamBaseUrl,
    getDamImagePicture,
    getRatiosAndSOurcesFromImageObj,
} from '../../../custom-hooks/use-dam-image'

const IMAGE_INTRO_DURATION_MS = 300

export const HeroBannerV2 = ({
    isMaskHeaderActive = true,
    textBlock,
    imageSet,
    slides,
    variant = 'white', // theme can either be black or white
    height = 'big',
    maxWidth = remCalc(1920),
    hasPaddings = true,
    isCarouselV2 = false,
    buttonText,
    buttonText2,
    bentoCards = [],
    countdown,
    isFirstElement = false,
    ...props
}) => {
    const CAROUSEL_DURATION = 5000
    const [activeSlideIndex, setActiveSlideIndex] = useState(0)
    const [slideProgress, setSlideProgress] = useState(0)
    const [isImageHovered, setIsImageHovered] = useState(false)
    const carouselAnimRef = useRef(null)
    const carouselTimerRef = useRef(null)
    const carouselStartRef = useRef(null)
    const mobileImageRef = useRef(null)
    const desktopImageRef = useRef(null)
    const pointerStartXRef = useRef(null)
    const pointerStartYRef = useRef(null)
    const pointerCurrentXRef = useRef(null)
    const pointerCurrentYRef = useRef(null)
    const pointerIdRef = useRef(null)
    const touchStartXRef = useRef(null)
    const touchStartYRef = useRef(null)
    const lastSwipeAtRef = useRef(0)
    const hasCarousel = Array.isArray(slides) && slides.length > 0
    const activeSlide = hasCarousel ? slides[activeSlideIndex] : null
    const SWIPE_THRESHOLD = 40
    const SWIPE_CLICK_SUPPRESSION_MS = 300

    // When carousel is active, derive all content from the current slide
    const activeTextBlock = activeSlide
        ? {
              label: activeSlide.textblock?.label,
              subtitle: activeSlide.textblock?.subtitle,
              title: activeSlide.textblock?.title,
              bodyCopy: activeSlide.textblock?.bodycopy,
              desktopAlignment: activeSlide.textblock?.desktopalignment,
              tabletMobileAlignment: activeSlide.textblock?.tabletmobilealignment,
          }
        : textBlock
    const activeButtonText = activeSlide?.button ?? buttonText
    const activeButtonText2 = activeSlide?.second_button ?? buttonText2
    const activeCountdown = activeSlide?.countdown?.length ? activeSlide.countdown : countdown
    const activeMaskHeaderActive = activeSlide
        ? !!activeSlide.maskgroup?.maskheaderactive
        : isMaskHeaderActive

    const goNext = useCallback(() => {
        setActiveSlideIndex((prev) => {
            const next = prev < slides.length - 1 ? prev + 1 : 0
            setSlideProgress(0)
            carouselStartRef.current = null
            return next
        })
    }, [slides?.length])

    const goToSlide = useCallback(
        (index) => {
            const clamped = Math.min(Math.max(index, 0), slides.length - 1)
            setActiveSlideIndex(clamped)
            setSlideProgress(0)
            carouselStartRef.current = null
        },
        [slides?.length],
    )

    const resetAutoPlay = useCallback(() => {
        cancelAnimationFrame(carouselAnimRef.current)
        clearTimeout(carouselTimerRef.current)
    }, [])

    const clearPointerState = () => {
        pointerStartXRef.current = null
        pointerStartYRef.current = null
        pointerCurrentXRef.current = null
        pointerCurrentYRef.current = null
        pointerIdRef.current = null
    }

    const clearTouchState = () => {
        touchStartXRef.current = null
        touchStartYRef.current = null
    }

    const handleSwipeByDelta = useCallback(
        (deltaX, deltaY) => {
            if (
                !hasCarousel ||
                Math.abs(deltaX) < SWIPE_THRESHOLD ||
                Math.abs(deltaX) <= Math.abs(deltaY)
            ) {
                return false
            }
            lastSwipeAtRef.current = Date.now()
            resetAutoPlay()
            if (deltaX < 0) {
                goNext()
                return true
            }
            goToSlide(activeSlideIndex - 1)
            return true
        },
        [activeSlideIndex, goNext, goToSlide, hasCarousel, resetAutoPlay],
    )

    // Keep links/buttons (including their children) out of carousel gesture and navigation handling.
    // Capturing their pointer on the carousel can retarget desktop clicks away from the CTA.
    const isInteractiveTarget = (target) =>
        target instanceof Element && !!target.closest('a, button')

    const handleClick = (e) => {
        if (!hasCarousel || isInteractiveTarget(e.target)) return
        if (Date.now() - lastSwipeAtRef.current < SWIPE_CLICK_SUPPRESSION_MS) {
            lastSwipeAtRef.current = 0
            return
        }
        resetAutoPlay()
        goNext()
    }

    const handlePointerDown = (e) => {
        if (
            !hasCarousel ||
            isInteractiveTarget(e.target) ||
            (e.pointerType === 'mouse' && e.button !== 0)
        )
            return
        pointerIdRef.current = e.pointerId
        pointerStartXRef.current = e.clientX
        pointerStartYRef.current = e.clientY
        pointerCurrentXRef.current = e.clientX
        pointerCurrentYRef.current = e.clientY
        if (e.currentTarget?.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId)
    }

    const handlePointerMove = (e) => {
        if (pointerIdRef.current !== e.pointerId) return
        pointerCurrentXRef.current = e.clientX
        pointerCurrentYRef.current = e.clientY
    }

    const handlePointerUp = (e) => {
        if (pointerIdRef.current !== e.pointerId) return
        if (pointerStartXRef.current === null || pointerStartYRef.current === null) {
            pointerIdRef.current = null
            return
        }
        const endX = pointerCurrentXRef.current ?? e.clientX
        const endY = pointerCurrentYRef.current ?? e.clientY
        const deltaX = endX - pointerStartXRef.current
        const deltaY = endY - pointerStartYRef.current
        clearPointerState()
        if (e.currentTarget?.releasePointerCapture) {
            try {
                e.currentTarget.releasePointerCapture(e.pointerId)
            } catch (_) {}
        }
        handleSwipeByDelta(deltaX, deltaY)
    }

    const handleTouchStart = (e) => {
        if (!hasCarousel || isInteractiveTarget(e.target)) return
        const touch = e.touches?.[0]
        if (!touch) return
        touchStartXRef.current = touch.clientX
        touchStartYRef.current = touch.clientY
    }

    const handleTouchEnd = (e) => {
        const touch = e.changedTouches?.[0]
        if (!touch || touchStartXRef.current === null || touchStartYRef.current === null) return
        const deltaX = touch.clientX - touchStartXRef.current
        const deltaY = touch.clientY - touchStartYRef.current
        clearTouchState()
        handleSwipeByDelta(deltaX, deltaY)
    }

    const handleKeyDown = (e) => {
        if (!hasCarousel || isInteractiveTarget(e.target)) return
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleClick(e)
        }
        if (e.key === 'ArrowRight') {
            cancelAnimationFrame(carouselAnimRef.current)
            clearTimeout(carouselTimerRef.current)
            goNext()
        }
        if (e.key === 'ArrowLeft') {
            cancelAnimationFrame(carouselAnimRef.current)
            clearTimeout(carouselTimerRef.current)
            goToSlide(activeSlideIndex - 1)
        }
    }

    useEffect(() => {
        if (!hasCarousel || slides.length <= 1) return

        const animate = (ts) => {
            if (!carouselStartRef.current) carouselStartRef.current = ts
            const pct = Math.min(((ts - carouselStartRef.current) / CAROUSEL_DURATION) * 100, 100)
            setSlideProgress(pct)

            if (pct < 100) {
                carouselAnimRef.current = requestAnimationFrame(animate)
            } else {
                carouselTimerRef.current = setTimeout(() => {
                    setActiveSlideIndex((prev) => {
                        const next = prev < slides.length - 1 ? prev + 1 : 0
                        setSlideProgress(0)
                        carouselStartRef.current = null
                        return next
                    })
                }, 0)
            }
        }

        carouselAnimRef.current = requestAnimationFrame(animate)

        return () => {
            cancelAnimationFrame(carouselAnimRef.current)
            clearTimeout(carouselTimerRef.current)
        }
    }, [activeSlideIndex, hasCarousel, slides?.length])

    const imageSetKey = `image_${height}`
    // props.image is the direct image field sent by herobanner_redesign_2026
    const imageSetObj = imageSet || props?.[imageSetKey] || props?.image
    // Carousel overrides the image with the active slide's image
    const activeImageSet = hasCarousel ? activeSlide?.image : imageSetObj

    const isSmallHeight = height === 'small'
    const isMediumHeight = height === 'mid'
    const styles = useStyleConfig('HeroBanner')
    const textStyles = useStyleConfig('HeroBannerText', {variant})
    const videoRef = useRef()

    const direction =
        activeTextBlock?.tabletMobileAlignment === 'bottom-center' ? 'column' : 'column-reverse'
    const verticalPadding = calculateVerticalPadding(direction, hasPaddings)
    const activeVideos = activeSlide?.videos ?? props?.videos
    const isVideo = activeVideos?.desktop && activeVideos?.mobile
    const followsRenditionSize = bentoCards.length === 0 && !isVideo && !!activeImageSet

    const gradient = getHerobannerGradient(
        activeTextBlock?.desktopAlignment,
        activeSlide?.maskgroup?.opacity ?? props?.maskgroup?.opacity,
        activeMaskHeaderActive,
    )

    const ratio = getAspectRatioObjectFromImageSet(activeImageSet)
    const toLandscapeRatio = (value, fallback) => {
        const parsed = Number(value)
        return Number.isFinite(parsed) && parsed > 1 ? parsed : fallback
    }
    const safeImageRatio = {
        base: toLandscapeRatio(ratio?.base, 1.172),
        md: toLandscapeRatio(ratio?.md, 3),
        lg: toLandscapeRatio(ratio?.lg, toLandscapeRatio(ratio?.md, 3)),
        xl: toLandscapeRatio(ratio?.xl, toLandscapeRatio(ratio?.lg, 3)),
    }
    const mobileImageGroup =
        !isVideo && activeImageSet ? createCompleteImageSet(activeImageSet) : null
    const [renditionRatio = {}, mobileImageSources] = mobileImageGroup
        ? getRatiosAndSOurcesFromImageObj(mobileImageGroup)
        : []
    // The picture's fallback source is its first available rendition.
    const imageRatio = followsRenditionSize
        ? {base: Object.values(renditionRatio)[0], ...renditionRatio}
        : safeImageRatio
    const mobileFallbackBreakpoint = mobileImageGroup
        ? ['desktop', 'tablet_1024', 'tablet_768', 'mobile'].find(
              (key) =>
                  mobileImageGroup[key]?.rendition && mobileImageGroup[key]?.dam_image?.[0]?.id,
          )
        : null
    const mobileFallbackUrl = mobileFallbackBreakpoint
        ? `${getDamBaseUrl()}/${mobileImageGroup[mobileFallbackBreakpoint].rendition}/assets/${
              mobileImageGroup[mobileFallbackBreakpoint].dam_image[0].id
          }`
        : null
    const desktopPicture =
        !isVideo && activeImageSet
            ? getDamImagePicture(
                  activeImageSet,
                  activeTextBlock?.title || 'hero-banner-v2-image',
                  isFirstElement,
                  imageRatio,
                  {
                      objectFit: 'cover',
                      display: 'block',
                  },
                  gradient ? {base: 'none', lg: gradient} : null,
              )
            : null

    const blurOverlayStyles = {
        filter: `blur(${remCalc(10)}) brightness(0.5)`,
        transform: 'scale(1.01)',
        maskImage: 'linear-gradient(transparent 45%, black 75%)',
        WebkitMaskImage: 'linear-gradient(transparent 45%, black 75%)',
    }
    const hasImageHoverZoom = bentoCards.length > 0 && !hasCarousel && !isVideo
    const imageHoverStyles = hasImageHoverZoom
        ? {
              transform: isImageHovered ? 'scale(1.1)' : 'scale(1)',
              transition: 'transform 0.4s ease',
          }
        : undefined

    useEffect(() => {
        if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return

        const animations = [mobileImageRef.current, desktopImageRef.current]
            .filter((el) => typeof el?.animate === 'function')
            .map((el) =>
                el.animate([{transform: 'scale(1.08)'}, {transform: 'scale(1)'}], {
                    duration: IMAGE_INTRO_DURATION_MS,
                    easing: 'ease-out',
                }),
            )

        return () => animations.forEach((animation) => animation.cancel())
    }, [activeSlideIndex])

    useEffect(() => {
        const videoElement = videoRef.current
        if (!videoElement) return

        const observerOptions = {
            root: null, // use the viewport as the reference
            threshold: 0.5,
        }

        const handleIntersection = (entries) => {
            entries.forEach(async (entry) => {
                if (entry.isIntersecting) {
                    try {
                        // Use async/await to better handle the play() Promise
                        await videoElement.play()
                    } catch (error) {
                        // Autoplay blocked by browser or interrupted by rapid scrolling
                        console.warn('Autoplay prevented:', error.message)
                    }
                }
            })
        }

        const observer = new IntersectionObserver(handleIntersection, observerOptions)
        observer.observe(videoElement)

        // Cleanup: use disconnect to completely shut down the observer
        return () => {
            observer.disconnect()
        }
    }, [])

    const heroBannerContent = (
        <Flex
            data-testid={`hero-banner-v2-container-${direction}`}
            flexDir={direction}
            pb={{base: 0, lg: verticalPadding}}
            maxW={maxWidth}
            position="relative"
            borderRadius={bentoCards.length > 0 ? 'large-1' : undefined}
            overflow={bentoCards.length > 0 ? 'hidden' : undefined}
            backgroundColor={
                variant === 'black' ? 'background.base.inverse' : 'background.base.default'
            }
            {...(hasCarousel ? styles.v2SwipeContainer : {})}
            onClick={hasCarousel ? handleClick : undefined}
            onKeyDown={hasCarousel ? handleKeyDown : undefined}
            onPointerDown={hasCarousel ? handlePointerDown : undefined}
            onPointerMove={hasCarousel ? handlePointerMove : undefined}
            onPointerUp={hasCarousel ? handlePointerUp : undefined}
            onPointerCancel={hasCarousel ? clearPointerState : undefined}
            onTouchStart={hasCarousel ? handleTouchStart : undefined}
            onTouchEnd={hasCarousel ? handleTouchEnd : undefined}
            onTouchCancel={hasCarousel ? clearTouchState : undefined}
            role={hasCarousel ? 'group' : undefined}
            tabIndex={hasCarousel ? 0 : undefined}
            {...styles.heroBannerContainer}
            {...styles.v2HeroBannerContainer}
        >
            {hasCarousel && slides.length > 1 && (
                <Flex
                    {...textStyles.v2CarouselBar}
                    display={{base: 'flex', md: 'none'}}
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    px={remCalc(12)}
                    zIndex={10}
                    pt={remCalc(8)}
                    pointerEvents="none"
                    aria-hidden="true"
                >
                    {slides.map((_, idx) => {
                        const barWidth =
                            idx < activeSlideIndex
                                ? '100%'
                                : idx === activeSlideIndex
                                ? `${slideProgress}%`
                                : '0%'
                        return (
                            <Box key={idx} {...textStyles.v2CarouselTrack}>
                                <Box {...textStyles.v2CarouselFill} style={{width: barWidth}} />
                            </Box>
                        )
                    })}
                </Flex>
            )}
            <Flex
                data-testid={
                    isVideo
                        ? 'hero-banner-v2-video'
                        : activeImageSet
                        ? 'hero-banner-v2-image'
                        : 'hero-banner-v2-text'
                }
                w="100%"
                maxW={maxWidth}
                position="relative"
                zIndex={1}
                overflow="hidden"
                onMouseEnter={hasImageHoverZoom ? () => setIsImageHovered(true) : undefined}
                onMouseLeave={hasImageHoverZoom ? () => setIsImageHovered(false) : undefined}
                {...styles.heroBannerMain}
                {...styles.v2HeroBannerMain}
                minH={followsRenditionSize ? {base: 'min-content', lg: 0} : 0}
                maxH={
                    followsRenditionSize
                        ? 'none'
                        : {
                              base: 'none',
                              md: bentoCards.length > 0 ? 'none' : remCalc(453),
                              lg: 'none',
                          }
                }
                h={
                    followsRenditionSize
                        ? 'auto'
                        : {
                              base: 'auto',
                              md: remCalc(bentoCards.length > 0 ? 572 : 453),
                              lg: 'auto',
                          }
                }
                aspectRatio={
                    followsRenditionSize
                        ? {...imageRatio, base: 58 / 103}
                        : {
                              base: bentoCards.length > 0 ? 85 / 151 : 58 / 103,
                              md: 'auto',
                              lg: 'auto',
                          }
                }
            >
                {!isVideo && mobileFallbackUrl && (
                    <HideOnDesktop
                        position="absolute"
                        inset={0}
                        zIndex={0}
                        overflow="hidden"
                        pointerEvents="none"
                    >
                        <Box ref={mobileImageRef} position="absolute" inset={0}>
                            <picture
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'block',
                                    ...imageHoverStyles,
                                }}
                            >
                                {mobileImageSources}
                                <img
                                    src={mobileFallbackUrl}
                                    alt={activeTextBlock?.title || 'hero-banner-v2-image'}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        display: 'block',
                                    }}
                                />
                            </picture>
                            {activeMaskHeaderActive && (
                                <Box
                                    position="absolute"
                                    inset="0"
                                    aria-hidden="true"
                                    sx={blurOverlayStyles}
                                >
                                    <picture
                                        style={{width: '100%', height: '100%', display: 'block'}}
                                    >
                                        {mobileImageSources}
                                        <img
                                            src={mobileFallbackUrl}
                                            alt=""
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                display: 'block',
                                            }}
                                        />
                                    </picture>
                                </Box>
                            )}
                        </Box>
                    </HideOnDesktop>
                )}

                {!isVideo && activeImageSet && (
                    <Box
                        display={{base: 'none', lg: 'block'}}
                        position="relative"
                        width="100%"
                        sx={{
                            '& [data-id="aspect-ratio-dam-image"]': {
                                aspectRatio: imageRatio,
                                maxH: followsRenditionSize
                                    ? 'none'
                                    : bentoCards.length > 0
                                    ? `calc(${remCalc(
                                          850,
                                      )} - 2 * var(--chakra-space-medium-base) - var(--hero-bottom-padding, 0px))`
                                    : `calc(${remCalc(850)} - var(--hero-bottom-padding, 0px))`,
                                _before: {display: 'none'},
                            },
                        }}
                        style={{'--hero-bottom-padding': verticalPadding || '0px'}}
                    >
                        <Box ref={desktopImageRef} position="relative" width="100%">
                            {hasImageHoverZoom ? (
                                <Box style={imageHoverStyles}>{desktopPicture}</Box>
                            ) : (
                                desktopPicture
                            )}
                            {activeMaskHeaderActive && (
                                <Box
                                    position="absolute"
                                    inset="0"
                                    pointerEvents="none"
                                    aria-hidden="true"
                                    sx={blurOverlayStyles}
                                >
                                    {desktopPicture}
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}

                {isVideo && (
                    <Box
                        w="100%"
                        minW={0}
                        flexShrink={1}
                        position={{base: 'absolute', lg: 'relative'}}
                        inset={{base: 0, lg: 'auto'}}
                        h={{base: '100%', lg: 'auto'}}
                        overflow="hidden"
                    >
                        {isFirstElement && (
                            <Helmet>
                                <link
                                    rel="preload"
                                    as="fetch"
                                    href={activeVideos?.mobile}
                                    // eslint-disable-next-line react/no-unknown-property
                                    fetchpriority="high"
                                    type="video/mp4"
                                ></link>
                            </Helmet>
                        )}
                        <Box
                            aspectRatio={
                                Object.keys(ratio).length > 0 ? ratio : {base: 1.172, md: 3}
                            }
                            {...styles.heroBannerVideo}
                            {...styles.v2HeroBannerVideo}
                            _after={{
                                background: {lg: gradient ? `${gradient}` : undefined},
                            }}
                            h={{base: '100%', lg: 'auto'}}
                        >
                            <video
                                playsInline
                                ref={videoRef}
                                autoPlay={videoRef.current?.started ?? isFirstElement}
                                muted
                                loop
                                aria-hidden="true"
                                style={{
                                    height: '100%',
                                    width: '100%',
                                    objectFit: 'cover',
                                }}
                                preload={'metadata'}
                            >
                                <source
                                    src={activeVideos?.mobile}
                                    type="video/mp4"
                                    media="(max-width: 1023px)"
                                />
                                <source
                                    src={activeVideos?.desktop}
                                    type="video/mp4"
                                    media="(min-width: 1024px)"
                                />
                                Your browser does not support the video tag.
                            </video>
                        </Box>
                    </Box>
                )}
                <HideOnMobile
                    position={{base: 'relative', lg: 'absolute'}}
                    height={{base: 'unset', lg: '100%'}}
                    width="100%"
                    {...styles.heroBannerDesktop}
                    {...styles.v2HeroBannerDesktop}
                >
                    <HeroBannerV2Text
                        variant={variant}
                        countdown={activeCountdown}
                        textBlock={activeTextBlock}
                        buttonText={activeButtonText}
                        buttonText2={activeButtonText2}
                        isBentoLayout={bentoCards.length > 0}
                        isCarouselV2={isCarouselV2 || hasCarousel}
                        isSmallHeight={isSmallHeight}
                        isMediumHeight={isMediumHeight}
                        isFirstElement={isFirstElement}
                        slidesCount={hasCarousel ? slides.length : 0}
                        activeSlideIndex={activeSlideIndex}
                        slideProgress={slideProgress}
                        onPrev={() => goToSlide(activeSlideIndex - 1)}
                        onNext={goNext}
                    />
                </HideOnMobile>

                <HideOnDesktop
                    {...styles.heroBannerMobile}
                    {...styles.v2HeroBannerMobile}
                    position="relative"
                    minH={isVideo ? '100%' : followsRenditionSize ? 'min-content' : 0}
                    zIndex={1}
                    w="100%"
                    flex={isVideo ? '1 0 auto' : followsRenditionSize ? '1 0 auto' : '1'}
                >
                    <HeroBannerV2Text
                        variant={variant}
                        maxWidth={maxWidth}
                        countdown={activeCountdown}
                        textBlock={activeTextBlock}
                        hasPaddings={hasPaddings}
                        buttonText={activeButtonText}
                        buttonText2={activeButtonText2}
                        isBentoLayout={bentoCards.length > 0}
                        isCarouselV2={isCarouselV2 || hasCarousel}
                        isFirstElement={isFirstElement}
                        slidesCount={hasCarousel ? slides.length : 0}
                        activeSlideIndex={activeSlideIndex}
                        slideProgress={slideProgress}
                        onPrev={() => goToSlide(activeSlideIndex - 1)}
                        onNext={goNext}
                    />
                </HideOnDesktop>
            </Flex>
        </Flex>
    )

    return (
        <Flex
            {...styles.v2Layout}
            {...(bentoCards.length > 0 ? styles.v2BentoLayout : {})}
            {...(followsRenditionSize ? {maxH: 'none'} : {})}
            maxW={maxWidth}
            w="100%"
            mx="auto"
        >
            <Box
                flex={{base: 'none', lg: '1 1 0%'}}
                w={{base: '100%', lg: 'auto'}}
                maxW={{md: bentoCards.length > 0 ? remCalc(704) : 'none', lg: 'none'}}
                minW={0}
            >
                {heroBannerContent}
            </Box>
            {bentoCards.length > 0 && (
                <Box {...styles.v2BentoColumn}>
                    <HeroBannerV2BentoBox cards={bentoCards} />
                </Box>
            )}
        </Flex>
    )
}

HeroBannerV2.propTypes = {
    isMaskHeaderActive: PropTypes.bool,
    textBlock: PropTypes.shape({
        label: PropTypes.string,
        subtitle: PropTypes.string,
        title: PropTypes.string,
        bodyCopy: PropTypes.string,
        desktopAlignment: PropTypes.string,
        tabletMobileAlignment: PropTypes.string,
    }),
    imageSet: PropTypes.object,
    image: PropTypes.object,
    slides: PropTypes.array,
    buttonText: PropTypes.object,
    buttonText2: PropTypes.object,
    bentoCards: PropTypes.array,
    variant: PropTypes.string,
    height: PropTypes.string,
    link: PropTypes.string,
    maxWidth: PropTypes.string,
    hasPaddings: PropTypes.bool,
    isCarouselV2: PropTypes.bool,
    countdown: PropTypes.array,
    maskgroup: PropTypes.shape({
        opacity: PropTypes.string,
    }),
    videos: PropTypes.shape({
        desktop: PropTypes.string,
        mobile: PropTypes.string,
    }),
    isFirstElement: PropTypes.bool,
}
